import React, { useState, useRef, useEffect } from 'react';
import * as styles from './Hero.module.scss';
import { TimelineContent, Dot } from '@components/Timeline';
import DashedLine from '@components/DashedLine';
import ActionButton from './ActionButton';
import Button from '@input/Button';
import PhoneNumber from '@components/PhoneNumber';
import Modal from '@components/Modal';
import Form from '@components/Form';
import useConfig from '@hooks/useConfig';
import tracker from '@utils/tracker';
import classNames from 'classnames';
import { formatParagraphs } from '@helpers';

export default React.memo(function Hero({ topWrapper }) {
  const config = useConfig();
  const hasBreadcrumbs = config.showBreadcrumbs && config.breadcrumbsHtml;

  return (
    <section
      className={classNames('section', styles.wrapper, {
        [styles.hasBreadcrumbs]: hasBreadcrumbs
      })}
    >
      <div className="content-wrapper">
        {hasBreadcrumbs && (
          <div
            className={classNames('breadcrumb-wrapper', styles.breadcrumbs)}
            dangerouslySetInnerHTML={{ __html: config.breadcrumbsHtml }}
          />
        )}
        <div className={styles.content}>
          <Text topWrapper={topWrapper} showPretitle={!hasBreadcrumbs} />
          <Links />
        </div>
      </div>
    </section>
  );
});

function Text({ topWrapper, showPretitle }) {
  const config = useConfig();

  const headline = config.headline
    .replace(
      /<strong[^>]*>([^<]*)<\/strong>/,
      '<span class="highlight-term">$1</span>'
    )
    .replace(/ <\/span>[^\b\w\d]*/, '</span> ')
    .replace(/ ([^<> ]*)$/, '&nbsp;$1');

  return (
    <div className={styles.textWrapper}>
      {showPretitle && (
        <span className="section-header">Today's Mortgage Rates</span>
      )}
      <h1
        className="section-headline"
        dangerouslySetInnerHTML={{ __html: headline }}
      />
      <TimelineContent
        className={styles.text}
        topWrapper={topWrapper}
        isTimelineStart
      >
        {formatParagraphs(config.introText)}
        {config.showHeroLink && config.heroLinkText && config.heroLinkUrl && (
          <div className={styles.cta}>
            <Button href={config.heroLinkUrl} showArrow>
              {config.heroLinkText}
            </Button>
          </div>
        )}
      </TimelineContent>
    </div>
  );
}

function Links() {
  const config = useConfig();

  const showApply = config.showApplyButton && config.applyButtonUrl;
  const showChat = config.showChatButton;
  const showConsultation = config.showRequestConsultationButton;
  const showPhoneNumber = config.showPhoneNumberButton;
  const showRateWatch = config.showRateWatchButton && config.rateWatchUrl;
  const showScheduleAppointment =
    config.showScheduleAppointmentButton && config.bookingsPageUrl;

  return (
    <div className={styles.linksWrapper}>
      <div className={styles.lineWrapper}>
        <Dot color="gray" />
        <DashedLine direction="horizontal" />
      </div>
      <div className={styles.linksBox}>
        <strong className="subheadline">Not sure where to start?</strong>
        <p>Our team of loan agents is here to help</p>

        <div className={styles.links}>
          {showScheduleAppointment && (
            <ScheduleAppointment
              buttonText={config.scheduleAppointmentText}
              modalText={config.scheduleAppointmentModalText}
              bookingsUrl={config.bookingsPageUrl}
            />
          )}

          {showApply && (
            <ActionButton icon="apply" href={config.applyButtonUrl}>
              {config.applyButtonText}
            </ActionButton>
          )}

          {showConsultation && (
            <RequestConsultation text={config.requestConsultationText} />
          )}

          {showChat && (
            <ActionButton icon="chat">{config.chatButtonText}</ActionButton>
          )}
          {showPhoneNumber && (
            <PhoneNumber
              component={ActionButton}
              num={config.phoneNumber}
              icon="call"
            />
          )}
          {showRateWatch && (
            <ActionButton icon="alerts" href={config.rateWatchUrl}>
              {config.rateWatchText}
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );
}

const ScheduleAppointment = React.memo(
  ({
    buttonText = 'Schedule an appointment',
    modalText = 'Schedule an appointment',
    bookingsUrl
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
      if (isOpen) {
        tracker.track(tracker.actions.OPEN_BOOKING_MODAL, {
          booking_page: bookingsUrl,
          is_agent: false
        });
      }
    }, [isOpen, bookingsUrl]);

    return (
      <>
        <ActionButton
          icon="calendar"
          aria-controls="calculator-schedule-modal"
          onClick={() => setIsOpen(true)}
        >
          {buttonText}
        </ActionButton>
        <Modal
          isOpen={isOpen}
          id="calculator-schedule-modal"
          header={modalText}
          className={styles.scheduleModal}
          contentIsScrollable={false}
          setIsOpen={setIsOpen}
        >
          <iframe
            title="Schedule an apoointment"
            className="booking-iframe"
            scrolling="yes"
            src={bookingsUrl}
          />
        </Modal>
      </>
    );
  }
);

const RequestConsultation = React.memo(({ text }) => {
  const [isOpen, setIsOpen] = useState(false);
  const modal = useRef();
  const scrollTimer = useRef();

  useEffect(() => {
    clearTimeout(scrollTimer.current);

    if (!isOpen) {
      scrollTimer.current = setTimeout(() => {
        modal.current?.resetScroll;
      }, 300);
    }
  }, [isOpen, modal]);

  return (
    <>
      <ActionButton
        icon="consultation"
        aria-controls="calculator-rate-modal"
        onClick={() => setIsOpen(true)}
      >
        {text}
      </ActionButton>
      <Modal
        isOpen={isOpen}
        id="calculator-consultation-modal"
        // ref={content}
        ref={modal}
        contentClassName={styles.consultationModal}
        header="Request a free consultation"
        setIsOpen={setIsOpen}
      >
        <div className={styles.consultationForm}>
          <Form
            selector="#mortgage-rate-calculator-hero-form"
            hasPrefillFields={false}
          />
        </div>
      </Modal>
    </>
  );
});
